import { ENV } from '@forwardflow/helpers/configs'

/**
 * THE ASSISTANT CONNECT HANDOFF.
 *
 * When someone connects the Forward Flow assistant to ChatGPT, the MCP server
 * sends them here to be identified. It cannot ask them to sign in to Nikaido —
 * they have no account there and have never heard of it — so this dashboard
 * proves who they are instead, and hands back the access token it already
 * holds. The MCP server calls `/users/me` with it: a 200 both proves the token
 * and supplies the organizations they may choose between.
 *
 * ONE PLACE owns validation, storage and the destination. Spreading those
 * across the route, the sign-in form and the post-login navigation is how a
 * "continuation" quietly becomes an open redirect in one of the three.
 */

/** `?handle=<opaque>` — the only parameter this feature reads. */
export const HANDLE_PARAM = 'handle'

/** Survives the round trip through Google or Microsoft. */
const STORAGE_KEY = 'assistant_connect_handle'

/**
 * base64url, 16 to 128 characters.
 *
 * A HANDLE, NEVER A URL. Accepting a URL here — even one that looks like ours —
 * would make this parameter a redirect target anyone could aim: open
 * `/connect/assistant?handle=https://evil.example` and the dashboard would
 * deliver a freshly authenticated user, and their token, to it. The pattern
 * excludes `:` and `/`, so no URL can satisfy it, and the destination is built
 * from configuration below rather than from anything the caller supplied.
 */
const HANDLE_PATTERN = /^[A-Za-z0-9_-]{16,128}$/

export const isValidHandle = (value: unknown): value is string =>
   typeof value === 'string' && HANDLE_PATTERN.test(value)

const rawHandleParam = (search: string | URLSearchParams): string | null => {
   const params =
      typeof search === 'string' ? new URLSearchParams(search) : search
   return params.get(HANDLE_PARAM)
}

/**
 * Remember it across an SSO round trip.
 *
 * `sessionStorage`, not `localStorage`: this is meaningful for one login in one
 * tab, and a value left in `localStorage` would silently hand a completely
 * unrelated login days later to a connection nobody asked for.
 */
export const rememberHandle = (handle: string): void => {
   if (!isValidHandle(handle)) return
   try {
      sessionStorage.setItem(STORAGE_KEY, handle)
   } catch {
      // Private mode, or storage disabled. The direct path still works from the
      // query string; only the SSO round trip loses the handoff, and losing it
      // means an ordinary login rather than a broken one.
   }
}

export const recallHandle = (): string | null => {
   try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      return isValidHandle(stored) ? stored : null
   } catch {
      return null
   }
}

export const clearHandle = (): void => {
   try {
      sessionStorage.removeItem(STORAGE_KEY)
   } catch {
      /* nothing to clear */
   }
}

/**
 * The handle for this login, from the URL or from an SSO round trip.
 *
 * CONSUMED: cleared as it is read, so it cannot leak into the next login in the
 * same tab.
 *
 * AN EXPLICIT PARAMETER IS THE ANSWER, even when it is a bad one. Treating a
 * malformed `?handle=` as absent would fall through to whatever is in
 * `sessionStorage`, resuming a handoff the request did not ask for and
 * contradicting the one thing this parameter promises about invalid input.
 */
export const consumeHandle = (
   search?: string | URLSearchParams
): string | null => {
   if (search !== undefined) {
      const raw = rawHandleParam(search)
      if (raw !== null) {
         clearHandle()
         return isValidHandle(raw) ? raw : null
      }
   }
   const handle = recallHandle()
   clearHandle()
   return handle
}

/**
 * Where the identity is posted.
 *
 * BUILT HERE, from the configured MCP origin and a fixed path. Nothing from the
 * query string reaches it except the handle, which the pattern above has
 * already restricted to characters that cannot form a URL.
 */
export const identityPostUrl = (): string | null => {
   const base = String(ENV.NIKAIDO_MCP_URL ?? '').replace(/\/+$/, '')
   if (!base) return null
   return `${base}/mcp-oauth/external-identity`
}

/**
 * Is a handoff waiting for this login to finish?
 *
 * PEEKS — it does not consume. The consuming read belongs to the route that
 * actually performs the handoff, so a navigation decision here cannot burn a
 * handle and leave the user on a dead page.
 */
export const peekHandle = (
   search?: string | URLSearchParams
): string | null => {
   if (search !== undefined) {
      const raw = rawHandleParam(search)
      if (raw !== null) return isValidHandle(raw) ? raw : null
   }
   return recallHandle()
}

export const hasPendingHandle = (search?: string | URLSearchParams): boolean =>
   peekHandle(search) !== null

/** Where the route lives. Fixed, and the only destination this feature has. */
export const CONNECT_ASSISTANT_PATH = '/connect/assistant'

export const assistantAuthPath = (
   path: '/signup' | '/signin',
   search: string | URLSearchParams,
): string => {
   const handle = peekHandle(search)
   if (handle) return `${path}?${new URLSearchParams({ handle })}`
   // Keep an explicit refusal from falling back to an older tab's handle.
   return rawHandleParam(search) !== null ? `${path}?handle=` : path
}

export const signupVerificationUrl = (
   origin: string,
   search: string | URLSearchParams,
): string => {
   const handle = peekHandle(search)
   // Email generation appends ?token=...; use a fixed path, not another query.
   // Unlike sessionStorage, this survives opening the email in a fresh tab.
   const path = handle
      ? `/signup/assistant/${encodeURIComponent(handle)}`
      : rawHandleParam(search) !== null ? '/signup/assistant/invalid' : '/signup'
   return new URL(path, origin).href
}
